import { useRef } from 'react';
import { useTranslation } from '@frontend/i18n';
import { BulkTextPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Heading, MessageTemplate, TagType, Text } from '@frontend/design-system';
import { DEFAULT_BULK_SHORT_URL } from './constants';

interface Props {
  charCountLimit?: number;
  error?: string;
  initialTemplate?: string;
  isNewTag?: boolean;
  onBlur?: () => void;
  onChange?: (template: string, preview: string) => void;
  previewOnly?: boolean;
  readOnly?: boolean;
  tags: TagType[];
}

export const BulkTextTemplateEditor = ({
  charCountLimit = 132,
  error,
  initialTemplate,
  isNewTag,
  onBlur,
  onChange,
  previewOnly = false,
  readOnly,
  tags,
}: Props) => {
  const { t } = useTranslation('bulk-messaging');
  const initialTemplateRef = useRef<string | undefined>(initialTemplate);

  return (
    <MessageTemplate
      disableEmoji
      draggableTags={true}
      initialTemplate={initialTemplateRef.current}
      isNewTag={isNewTag}
      onChange={onChange}
      readOnly={readOnly}
      shortenUrlsTo={DEFAULT_BULK_SHORT_URL}
      tags={tags}
    >
      {!previewOnly && (
        <>
          <MessageTemplate.Editor
            className='bulk-text-template-editor'
            charCount
            charCountLimit={charCountLimit}
            data-testid='bulk-message-template-input'
            data-trackingid={`${BulkTextPrefixes.Editor}-message-input`}
            error={error}
            onBlur={onBlur}
            placeholder={t('Enter Your Bulk Message')}
          />
          <Text
            size='small'
            color='light'
            css={{
              margin: theme.spacing(1, 0),
            }}
          >
            {t('You can also drag and drop or click a tag to add to your message')}
          </Text>
          <MessageTemplate.TagList
            css={{
              margin: theme.spacing(1, 0),
            }}
          />
          <Heading
            level={3}
            css={{
              marginTop: theme.spacing(4),
            }}
          >
            {t('Message Preview')}
          </Heading>
        </>
      )}
      <MessageTemplate.Preview
        className='bulk-text-template-preview'
        appendWith='Reply STOP to unsubscribe' // This should not be translated because the backend, which injects this text, isn't translated.
        css={{
          margin: theme.spacing(1, 0),
        }}
        preserveWhitespace
      />
    </MessageTemplate>
  );
};
